<script setup lang="ts">

import { Window } from '@progress/kendo-vue-dialogs';
import { createFocusTrap, FocusTrap, Options as FocusTrapOptions } from 'focus-trap';
import { ref, onMounted, onUnmounted } from 'vue';

import CloseButton from '@/components/elements/CloseButton.vue';
import FontAwesomeIcon from '@/components/elements/FontAwesomeIcon.vue';
import {
  PopupZIndexProvider, resolveStackingZIndex, DEFAULT_DIALOGS_ZINDEX,
} from '@/components/popupZIndex';
import { querySelector, QuerySelector } from '@/utils/querySelector';

interface HeaderIcon {
  dataTestid?: string;
  type: string;
  name: string;
  fw?: boolean;
  size?: string | number;
}

// Props.
interface Props {
  initialFocus?: QuerySelector,
  dataTestid?: string,
  height?: number,
  returnFocus?: QuerySelector,
  title?: string,
  width?: number,
  headerIcon?: HeaderIcon,
  appendClass?: string,
}
const props = withDefaults(defineProps<Props>(), {
  initialFocus: 'input',
  dataTestid: 'modal',
  height: 490,
  returnFocus: undefined,
  title: '',
  width: 600,
  headerIcon: undefined,
  appendClass: undefined,
});
/**
 * Attributes are forwarded explicitly to the Kendo `Window` (see `v-bind="$attrs"`
 * in the template), so automatic fallthrough is disabled. Required because the
 * component root is now the stable anchor `<div>` rather than the Window itself —
 * without this, every attribute would be applied twice, once to the anchor and
 * once to the Window.
 */
defineOptions({ inheritAttrs: false });

const $window = ref<InstanceType<typeof Window>>();
let focusTrap: FocusTrap;

/**
 * z-index handed to popups (dropdowns, calendars, time pickers) rendered inside
 * this modal. Kendo hardcodes 10002 for descendant popups, so they land at 10003
 * and open *behind* the modal whenever the modal itself sits higher than that.
 * Resolved from the rendered DOM in onMounted because Kendo assigns the modal's
 * z-index and no explicit prop is passed by any caller.
 */
const popupZIndex = ref(DEFAULT_DIALOGS_ZINDEX);

const emit = defineEmits(['close']);

const windowClasses = ref('k-window-modal');

onMounted(() => {
  const wrapper = (($window.value as InstanceType<typeof Window>).$refs.wrapper as HTMLDivElement)
    .querySelector('.k-window') as HTMLDivElement;

  wrapper.tabIndex = -1;
  // Keep popups just above this modal rather than at Kendo's hardcoded 10003.
  popupZIndex.value = resolveStackingZIndex(wrapper);
  const options: FocusTrapOptions = {
    allowOutsideClick: true,
    fallbackFocus: querySelector('[aria-label="Close"]', wrapper),
    initialFocus: querySelector(props.initialFocus, wrapper),
    returnFocusOnDeactivate: props.returnFocus !== false,
    setReturnFocus: querySelector(props.returnFocus),
  };
  focusTrap = createFocusTrap(wrapper, options);
  if (options.initialFocus) {
    focusTrap.activate();
  }
  // Handle Kendo date/time picker button clicks. This is necessary to ensure that the widget
  // that is attached to the body doesn't "flicker" because of the focus-trap. Also, ignore test coverage for this
  // as it is extremely difficult to do achieve with all the optional chain branching that occurs.
  // @see https://zolldata.atlassian.net/browse/ZD-8802
  /* istanbul ignore next */
  wrapper
    .querySelectorAll('.k-datepicker .k-button, .k-timepicker .k-button, .k-datetimepicker .k-button')
    .forEach((button) => {
      button.addEventListener('click', (event) => {
        /* istanbul ignore next */
        (event.target as HTMLButtonElement).closest('.k-input')?.querySelector('input')?.focus();
      });
    });

  const winWrapper = <HTMLDivElement | undefined> $window.value?.$refs.wrapper;
  if (winWrapper) {
    winWrapper.className = windowClasses.value;
  }
});
// Deactivate focusTrap and restore focus to previous element (if any).
onUnmounted(() => {
  focusTrap.deactivate();
});
defineExpose({ $window });
</script>
<template>
  <!--
    Stable anchor element. Kendo's `Window` relocates its own root element with a
    raw `appendChild` into `appendTo` on mount, and calls `.remove()` on it in
    `beforeUnmount` — both behind Vue's back. If the Window were this component's
    root, Vue's vnode tree would still record that relocated element as a child of
    the parent container, and Vue would use it as the insertion anchor for
    subsequent siblings. `getNextHostNode()` would then hand back a node from
    `<body>`, and inserting the next sibling into the original container throws
    `NotFoundError: ... the node before which the new node is to be inserted is not
    a child of this node`, leaving the patch half-applied so the modal never shows.

    Keeping an element Kendo never touches as the root means Vue's anchor is always
    valid: the Window is relocated out of this div, so Vue inserts with a null
    anchor (a plain append) and reads `parentNode`/`nextSibling` off this div rather
    than off a moved or already-removed node.

    This div is intentionally left empty at runtime and is display:contents so it
    generates no box of its own.
  -->
  <div class="zo-modal--anchor">
    <Window
      ref="$window"
      append-to="body"
      :data-testid="props.dataTestid"
      :minimize-button="false"
      :title-render="`titleRender`"
      :close-button="`closeButton`"
      :maximize-button="false"
      :resizable="false"
      :modal="true"
      :initial-width="props.width"
      :window-style="{ height: 'none'}"
      :window-class="appendClass!"
      v-bind="$attrs"
    >
      <template #closeButton>
        <CloseButton
          class="zo-controls zo-modal--controls"
          :qa-id="`${props.dataTestid}_header_close_button`"
          aria-label="Close"
          @clicked="emit('close')"
        />
      </template>
      <template #titleRender>
        <header
          class="k-header text--ellipsis"
        >
          <h2 class="flex align-items-center">
            <FontAwesomeIcon
              v-if="props.headerIcon?.name"
              v-bind="{ ...props.headerIcon, placement: undefined }"
              :data-testid="`${props.dataTestid}_header_icon_before`"
              class="standard-icon-color"
            />
            <span
              class="text--ellipsis"
              :data-testid="`${props.dataTestid}_header`"
            > {{ props.title }} </span>
          </h2>
        </header>
      </template>
      <PopupZIndexProvider :z-index="popupZIndex">
        <slot />
      </PopupZIndexProvider>
    </Window>
  </div>
</template>

<style lang="scss">
.k-window-modal {
  .k-window-titlebar {
    user-select: none;
  }
}

// Generates no box, so inserting this anchor into any layout (flex, grid or block)
// is inert. Kendo moves the Window out of it on mount, leaving it empty.
.zo-modal--anchor {
  display: contents;
}
</style>
